import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProcessoContext } from '../../../Routes/context/ProcessoUsuarioContext';
import { StatusBanca } from '../../../model/banca/Banca';
import MembroBanca, { MembroBancaUtils, StatusMembroNome } from '../../../model/banca/MembroBanca';
import { FaUserCheck, FaUsers, FaChevronDown, FaChevronUp, FaPhone, FaEnvelope, FaIdCard, FaTimes, FaEye } from 'react-icons/fa';
import { BsPersonLinesFill } from 'react-icons/bs';
import { MembrosBancaViewModel } from '../../../viewmodel/banca/MembrosBancaViewModel';
import { VinculoNome } from '../../../model/Pessoa';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';
import ActionButton from './ActionButton';
import { getButtonConfigFromBackend } from '../utils/bancaButtonConfigUtils';

interface ComposicaoBancaModalProps {
  banca: any;
  onClose: () => void;
  isConsultarDisponibilidadeDisabled: boolean;
  isDefinicaoMembrosDisabled: boolean;
  hasMembrosConfirmados: boolean;
  hasTodosMembrosConsultados: boolean;
  membros: MembroBanca[];
}

const ComposicaoBancaModal: React.FC<ComposicaoBancaModalProps> = ({
  banca,
  onClose,
  isConsultarDisponibilidadeDisabled,
  isDefinicaoMembrosDisabled,
  hasMembrosConfirmados,
  hasTodosMembrosConsultados,
  membros,
}) => {
  const [membrosRecebidos, setMembrosRecebidos] = useState<MembroBanca[]>([]);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const navigate = useNavigate();
  const processo = useProcessoContext();

  const tipoBancaProps = banca.tipoBanca.nome;

  const handleDisponibilidade = () => {
    navigate(`/processos/${processo.processo?.id}/bancas/${banca.id}/disponibilidade-banca`, {
      state: { tipoBancaProps },
    });
    onClose();
  };

  const handleDefinirMembros = () => {
    navigate(`/processos/${processo.processo?.id}/bancas/${banca.id}/definir-membros`);
    onClose();
  };

  const handleVisualizacaoDetalhada = () => {
    navigate(`/processos/${processo.processo?.id}/bancas/${banca.id}/composicao`);
    onClose();
  };

  const toggleMemberDetails = (memberId: string) => {
    setExpandedMember(expandedMember === memberId ? null : memberId);
  };

  useEffect(() => {
    const fetchMembros = async () => {
      try {
        const getMembros = await MembrosBancaViewModel.getMembrosCompletos(processo.processo?.id!, banca.id);
        setMembrosRecebidos(getMembros);
      } catch (error) {
        console.error("Error fetching members:", error);
      }
    };
    fetchMembros();
  }, []);

  const getStatusBadgeStyle = (status: StatusMembroNome) => {
    const styles = {
      [StatusMembroNome.CONSULTADO]: { backgroundColor: '#ffc107', color: '#212529' },
      [StatusMembroNome.CONFIRMADO]: { backgroundColor: '#0dcaf0', color: 'white' },
      [StatusMembroNome.DEFINIDO]: { backgroundColor: '#0d6efd', color: 'white' },
      [StatusMembroNome.CONVOCADO]: { backgroundColor: '#198754', color: 'white' },
      [StatusMembroNome.CONVOCACAO_CONFIRMADA]: { backgroundColor: '#198754', color: 'white' }
    };
    return styles[status] || { backgroundColor: '#6c757d', color: 'white' };
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '800px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 30px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            Composição de Banca
            <FaUsers style={{ color: '#6c757d' }} />
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '30px', maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
          <ModalHelp text="Visualize a composição atual da banca. Use Consultar Disponibilidade para iniciar a seleção de membros ou Definir Membros para confirmar os definitivos." />
          {/* Botões de ação */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ marginBottom: '15px' }}>
              <ActionButton 
                config={getButtonConfigFromBackend(banca, 'consultarBanca')} 
                onClick={handleDisponibilidade}
                customIcon={<FaUserCheck />}
                customTitle="Consultar Disponibilidade"
                showText={true}
                checkSize="28px"
                customStyle={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: banca.status === StatusBanca.INDEFINIDA ? '#212529' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              />
            </div>
            
            <div>
              <ActionButton 
                config={getButtonConfigFromBackend(banca, 'definirBanca')} 
                onClick={handleDefinirMembros}
                customIcon={<BsPersonLinesFill />}
                customTitle="Definição de Membros"
                showText={true}
                checkSize="28px"
                customStyle={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: banca.status !== StatusBanca.INDEFINIDA ? '#212529' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              />
            </div>
          </div>

          {/* Lista de Membros */}
          <div>
            <h4 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
              Membros da Banca
            </h4>
            
            {!(membrosRecebidos.length > 0) ? (
              <div style={{ 
                color: '#6c757d', 
                fontSize: '14px', 
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                Não há membros definidos
              </div>
            ) : (
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                {MembroBancaUtils.ordenarMembros(membrosRecebidos).map((membro, index) => (
                  <div key={membro.id}>
                    <div
                      onClick={() => toggleMemberDetails(membro.id!)}
                      style={{
                        padding: '15px',
                        cursor: 'pointer',
                        borderBottom: index < membrosRecebidos.length - 1 ? '1px solid #eee' : 'none',
                        borderLeft: membro.isPresidente ? '3px solid #007bff' : 'none',
                        backgroundColor: expandedMember === membro.id ? '#f8f9fa' : 'white',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'center',
                        gap: '16px'
                      }}
                    >
                      {/* Nome */}
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '14px'
                        }}>
                          {membro.pessoa.nome}
                          {membro.isPresidente && ` (Presidente)`}
                        </div>
                      </div>

                      {/* Status, Papel e Ícone */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        {/* Status Badge */}
                        <span
                          style={{
                            ...getStatusBadgeStyle(membro.statusMembro),
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            minWidth: '100px',
                            textAlign: 'center'
                          }}
                        >
                          {membro.statusMembro}
                        </span>

                        {/* Papel */}
                        <span style={{
                          color: '#6c757d',
                          border: '1px solid #6c757d',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          minWidth: '60px',
                          textAlign: 'center'
                        }}>
                          {MembroBancaUtils.papelToAbreviacao(membro.papelMembro.nome)}
                        </span>

                        {/* Ícone */}
                        <div style={{ width: '24px', textAlign: 'center' }}>
                          {expandedMember === membro.id ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>
                    </div>

                    {/* Detalhes expandidos */}
                    {expandedMember === membro.id && (
                      <div style={{
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderTop: '1px solid #eee'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '20px'
                        }}>
                          <div>
                            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              <FaIdCard style={{ marginRight: '8px', color: '#6c757d' }} />
                              <strong>CPF:</strong> {membro.pessoa.cpf || 'Não informado'}
                            </p>
                            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              <FaEnvelope style={{ marginRight: '8px', color: '#6c757d' }} />
                              <strong>Email:</strong> {membro.pessoa.email || 'Não informado'}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              <FaPhone style={{ marginRight: '8px', color: '#6c757d' }} />
                              <strong>Telefone:</strong> {membro.telefone || 'Não informado'}
                            </p>
                            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              <strong>Matrícula/RA:</strong> {membro.pessoa.matricula || 'Não informada'}
                            </p>
                          </div>
                        </div>
                        <div style={{ marginTop: '15px' }}>
                          <small style={{ color: '#6c757d', fontSize: '12px' }}>
                            {membro.isDefinidoEmBancaDeOutroTipo
                              ? 'Este membro está definido em outra banca de tipo diferente deste Processo'
                              : 'Não participa de outras bancas neste Processo'}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Botão Visualização Detalhada - Discreto na parte inferior */}
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
            <button
              onClick={handleVisualizacaoDetalhada}
              style={{
                background: 'none',
                border: 'none',
                color: '#6c757d',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                textDecoration: 'underline'
              }}
            >
              <FaEye style={{ fontSize: '10px' }} />
              Visualização detalhada
            </button>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default ComposicaoBancaModal;